<template>
  <button
    class="mb-4 w-full rounded-lg border border-gray-200 bg-white p-4 text-gray-700 shadow-sm transition-shadow duration-200 hover:shadow-md md:p-6"
    @click="onClick"
  >
    <div class="mb-2 flex items-center justify-between md:mb-4">
      <div class="flex flex-col items-start">
        <div class="flex items-center space-x-2">
          <h3 class="text-lg font-semibold text-gray-700 md:text-xl">Cotización #{{ preOrder.id }}</h3>
          <clipboard-button
            :text="preOrderUrl"
            text-name="URL de la cotización"
            class="text-sm text-gray-500 md:text-base"
          />
        </div>
        <p class="text-sm text-gray-500 md:text-base">
          {{ preOrder.contactEmail }}
        </p>
      </div>
      <div class="flex flex-col items-start">
        <div class="flex items-center space-x-3">
          <span
            class="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium"
            :class="{
              'bg-green-100 text-green-800': preOrder.status === 'sent',
              'bg-yellow-100 text-yellow-800': preOrder.status === 'accepted',
              'bg-red-100 text-red-800': preOrder.status === 'rejected' || preOrder.status === 'expired',
              'bg-blue-100 text-blue-800': preOrder.status === 'converted',
              'bg-gray-100 text-gray-800': !['sent', 'accepted', 'rejected', 'expired', 'converted'].includes(
                preOrder.status,
              ),
            }"
          >
            {{ t(`preOrder.status.${preOrder.status}`) }}
          </span>
          <button
            class="flex items-center justify-center rounded-full p-1 transition-colors hover:bg-gray-100"
            @click.prevent.stop="toggleExpanded"
          >
            <ChevronDownIcon
              class="size-5 text-gray-700 transition-transform duration-200"
              :class="{ 'rotate-180': isExpanded }"
            />
          </button>
        </div>
        <p class="text-right text-sm text-gray-500 md:text-base">
          {{ useTimeToDate(preOrder.createdAt) }}
        </p>
      </div>
    </div>

    <div
      v-if="isExpanded"
      class="space-y-4"
    >
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <h4 class="mb-2 font-medium text-gray-700">Información de Contacto</h4>
          <div class="space-y-1 text-sm text-gray-500">
            <p><span class="font-medium">Nombre:</span> {{ preOrder.contactName }}</p>
            <p><span class="font-medium">Email:</span> {{ preOrder.contactEmail }}</p>
            <p v-if="preOrder.contactNumber"><span class="font-medium">Teléfono:</span> {{ preOrder.contactNumber }}</p>
          </div>
        </div>
        <div>
          <h4 class="mb-2 font-medium text-gray-700">Información Comercial</h4>
          <div class="space-y-1 text-sm text-gray-500">
            <p><span class="font-medium">RUT:</span> {{ rutFormat(preOrder.businessRut) }}</p>
            <p><span class="font-medium">Razón Social:</span> {{ preOrder.socialReason }}</p>
            <p><span class="font-medium">Tipo de Tienda:</span> {{ t(`shop.kind.${camelCase(preOrder.shopKind)}`) }}</p>
          </div>
        </div>
        <div class="col-span-1 md:col-span-2">
          <h4 class="mb-2 font-medium text-gray-700">Dirección</h4>
          <div class="text-sm text-gray-500">
            <p>{{ preOrder.address }}, {{ preOrder.communeName }}, {{ preOrder.regionName }}</p>
          </div>
        </div>
        <div v-if="preOrder.expiresAt">
          <h4 class="mb-2 font-medium text-gray-700">Fecha de Vencimiento</h4>
          <p class="text-sm text-gray-500">
            {{ useTimeToDate(preOrder.expiresAt) }}
          </p>
        </div>
        <div v-if="preOrder.notes">
          <h4 class="mb-2 font-medium text-gray-700">Notas</h4>
          <p class="line-clamp-2 text-sm text-gray-700 md:line-clamp-3">
            {{ preOrder.notes }}
          </p>
        </div>
      </div>
      <div
        v-if="preOrder.convertedOrderId"
        class="border-t pt-4"
      >
        <p class="text-sm text-gray-500">
          <span class="font-medium">Convertida a Orden:</span>
          <a
            :href="`/orders/${preOrder.convertedOrderId}`"
            class="text-blue-600 underline hover:text-blue-800"
          >
            #{{ preOrder.convertedOrderId }}
          </a>
          <span
            v-if="preOrder.convertedAt"
            class="ml-2"
          >
            el {{ useTimeToDate(preOrder.convertedAt) }}
          </span>
        </p>
      </div>
    </div>
  </button>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { rutFormat } from 'rut-helpers';
import { useI18n } from 'vue-i18n';
import { camelCase } from 'lodash';
import useTimeToDate from './use/time-to-date';
import ClipboardButton from './shared/clipboard-button.vue';

const props = defineProps<{
  preOrder: PreOrder;
}>();

const isExpanded = ref(false);

const { t } = useI18n();

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}

const protocol = computed(() => (process.env.APPLICATION_HOST?.includes('http') ? '' : 'https://'));
const host = ref(`${protocol.value}${process.env.APPLICATION_HOST}`);
const preOrderUrl = computed(() => `${host.value}/pre-orders/${props.preOrder.accessToken}`);

const emit = defineEmits<{
  'set-pre-order': [preOrder: PreOrder];
}>();

function onClick() {
  if (props.preOrder.status === 'draft') {
    emit('set-pre-order', props.preOrder);
  } else {
    isExpanded.value = !isExpanded.value;
  }
}
</script>
